import { useState } from 'react';
import type { Product } from '@/mocks/products';

function formatPrice(amount: number): string {
  return `KSh ${amount.toLocaleString('en-KE')}`;
}

function getDiscountPercent(price: number, salePrice: number | null): number | null {
  if (!salePrice || salePrice >= price) return null;
  return Math.round(((price - salePrice) / price) * 100);
}

export default function ProductCard({
  product,
  variant = 'default',
  showProgressBar = false,
}: {
  product: Product;
  variant?: 'default' | 'compact';
  showProgressBar?: boolean;
}) {
  const [imageLoaded, setImageLoaded] = useState(false);

  const discount = getDiscountPercent(product.price, product.salePrice);
  const displayPrice = product.salePrice ?? product.price;
  const originalPrice = product.salePrice ? product.price : null;

  if (variant === 'compact') {
    const itemsLeft = product.stock;
    const maxStock = Math.max(itemsLeft + product.soldCount, 100);
    const stockPercent = Math.min((itemsLeft / maxStock) * 100, 100);

    return (
      <a
        href={`/product/${product.slug}`}
        className="group flex flex-col bg-white rounded-none overflow-hidden"
      >
        {/* Image */}
        <div className="relative aspect-square overflow-hidden bg-white">
          <img
            src={product.images[0]}
            alt={product.name}
            onLoad={() => setImageLoaded(true)}
            className={`w-full h-full object-contain object-center transition-transform duration-300 group-hover:scale-105 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading="lazy"
          />
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-100 animate-pulse" />
          )}

          {/* Discount badge - top right */}
          {discount && discount > 0 && (
            <span className="absolute top-2 right-2 px-2 py-0.5 bg-primary-500 text-white text-xs font-bold rounded-sm">
              -{discount}%
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 p-2 gap-1">
          {/* Title */}
          <h3 className="text-sm text-foreground-900 line-clamp-2 leading-snug min-h-[2.5rem]">
            {product.name}
          </h3>

          {/* Price */}
          <div className="flex flex-col mt-auto">
            <span className="text-base font-bold text-foreground-950">
              {formatPrice(displayPrice)}
            </span>
            {originalPrice && (
              <span className="text-sm text-foreground-500 line-through">
                {formatPrice(originalPrice)}
              </span>
            )}
          </div>

          {/* Items left progress bar */}
          {showProgressBar && (
            <div className="mt-1">
              <span className="text-xs text-foreground-600">
                {itemsLeft} items left
              </span>
              <div className="w-full h-1.5 bg-gray-200 rounded-full mt-1 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all"
                  style={{ width: `${stockPercent}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </a>
    );
  }

  return (
    <a
      href={`/product/${product.slug}`}
      className="group relative flex flex-col bg-white dark:bg-background-100 rounded-xl md:rounded-2xl border border-background-200/60 hover:border-primary-200 hover:shadow-lg hover:shadow-primary-100/30 transition-all duration-300 overflow-hidden"
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden bg-background-100">
        <img
          src={product.images[0]}
          alt={product.name}
          onLoad={() => setImageLoaded(true)}
          className={`w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
        />
        {!imageLoaded && (
          <div className="absolute inset-0 bg-background-200/70 animate-pulse" />
        )}

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {discount && discount > 0 && (
            <span className="px-2 py-0.5 rounded-md bg-accent-500 text-foreground-950 text-xs font-bold">
              -{discount}%
            </span>
          )}
          {product.stock <= 10 && product.stock > 0 && (
            <span className="px-2 py-0.5 rounded-md bg-secondary-500 text-background-50 text-xs font-bold">
              Low Stock
            </span>
          )}
        </div>

        {/* Wishlist */}
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-background-50/90 backdrop-blur-sm flex items-center justify-center shadow-sm hover:bg-background-50 transition-colors"
          aria-label="Add to wishlist"
        >
          <i className="ri-heart-line text-foreground-600 text-base transition-colors" />
        </button>

        {/* Sold count */}
        {product.soldCount > 500 && (
          <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-background-950/70 backdrop-blur-sm text-background-50 text-xs">
            {product.soldCount.toLocaleString('en-KE')} sold
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-3 md:p-4 gap-1.5">
        <span className="text-xs text-foreground-500 font-medium">{product.categoryName}</span>
        <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-700 transition-colors min-h-[2.5rem]">
          {product.name}
        </h3>
        <span className="text-xs text-foreground-500">{product.storeName}</span>
        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <i
                key={star}
                className={`ri-star-fill text-xs ${
                  star <= Math.round(product.rating)
                    ? 'text-accent-500'
                    : 'text-background-300'
                }`}
              />
            ))}
          </div>
          <span className="text-xs text-foreground-500">({product.reviewCount})</span>
        </div>
        <div className="flex items-baseline gap-2 mt-auto pt-1">
          <span className="text-base md:text-lg font-bold text-primary-600">{formatPrice(displayPrice)}</span>
          {originalPrice && (
            <span className="text-sm text-foreground-500 line-through">{formatPrice(originalPrice)}</span>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          className="mt-1 w-full py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 bg-primary-50 text-primary-700 hover:bg-primary-500 hover:text-background-50"
        >
          <i className="ri-shopping-cart-line text-sm" />
          Add to Cart
        </button>
      </div>
    </a>
  );
}